import { useEffect, useMemo, useState, useTransition } from 'react';
import { useQuery } from '@/lib/api-hooks';
import { motion } from 'framer-motion';
import {
  ClipboardList,
  GitBranch,
  Headphones,
  Phone,
  PhoneCall,
  Server,
} from 'lucide-react';
import { backendApi } from '@/lib/backend';
import { useModuleAccess, type TopLevelModule } from '@/lib/useModuleAccess';
import { Card } from '@/components/ui/Card';
import { ExtensionsTab } from './ExtensionsTab';
import { TrunksTab } from './TrunksTab';
import { IvrsTab } from './IvrsTab';
import { RoutesTab } from './RoutesTab';
import { PhonesTab } from './PhonesTab';
import { AuditTab } from './AuditTab';

type ProvisioningTab = 'extensions' | 'trunks' | 'ivrs' | 'routes' | 'phones' | 'audit';

const TAB_CONFIG: Array<{
  id: ProvisioningTab;
  label: string;
  icon: typeof Phone;
  module: TopLevelModule;
}> = [
  { id: 'extensions', label: 'Extensions', icon: PhoneCall, module: 'extensions' },
  { id: 'trunks', label: 'Trunks', icon: Server, module: 'trunks' },
  { id: 'ivrs', label: 'IVRs', icon: Headphones, module: 'ivrs' },
  { id: 'routes', label: 'Routes', icon: GitBranch, module: 'routes' },
  { id: 'phones', label: 'Phones', icon: Phone, module: 'phones' },
  { id: 'audit', label: 'Audit', icon: ClipboardList, module: 'auditLog' },
];

interface ProvisioningLayoutProps {
  customerId: string;
}

function TabAccessGate({
  module,
  children,
}: {
  module: TopLevelModule;
  children: React.ReactNode;
}) {
  const { canView, loading } = useModuleAccess(module);

  if (loading) {
    return <p className="text-sm text-gray-500 dark:text-gray-400 py-8">Checking access...</p>;
  }

  if (!canView) {
    return (
      <Card animate={false}>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          You do not have access to this provisioning module.
        </p>
      </Card>
    );
  }

  return <>{children}</>;
}

export function ProvisioningLayout({ customerId }: ProvisioningLayoutProps) {
  const { session, isStaff } = useModuleAccess('extensions');
  const customersNav = useQuery(
    backendApi.tenancy.listCustomersNav,
    isStaff ? {} : 'skip'
  ) as Array<{ id: string; name: string }> | undefined;

  const [selectedCustomerId, setSelectedCustomerId] = useState(customerId);
  const [activeTab, setActiveTab] = useState<ProvisioningTab>('extensions');
  const [isPending, startTransition] = useTransition();

  useEffect(() => {
    setSelectedCustomerId(customerId);
  }, [customerId]);

  const visibleTabs = useMemo(() => {
    if (!session?.moduleAccess) {
      return isStaff ? TAB_CONFIG : [];
    }

    if (isStaff) return TAB_CONFIG;

    return TAB_CONFIG.filter((tab) => {
      const level = session.moduleAccess.modules[tab.module];
      return level === 'view' || level === 'manage';
    });
  }, [isStaff, session]);

  useEffect(() => {
    if (visibleTabs.length === 0) return;
    if (!visibleTabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(visibleTabs[0].id);
    }
  }, [activeTab, visibleTabs]);

  const customerName =
    customersNav?.find((c) => c.id === selectedCustomerId)?.name ||
    session?.customer?.name ||
    'Customer';

  if (visibleTabs.length === 0) {
    return (
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">PBX Provisioning</h2>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          No provisioning modules are enabled for your account.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-gray-900 dark:text-white sm:text-4xl">PBX Provisioning</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Manage extensions, trunks, IVRs, routes, and phones
          </p>
        </div>

        {/* Staff customer switch lives in StaffExperience shell; show name only here. */}
        <div className="text-sm text-gray-500 dark:text-gray-400">
          <span className="font-medium text-gray-900 dark:text-white">{customerName}</span>
        </div>
      </div>

      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex flex-wrap gap-1" aria-label="Provisioning tabs">
          {visibleTabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;

            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => startTransition(() => setActiveTab(tab.id))}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  isActive
                    ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      <motion.div
        key={`${selectedCustomerId}-${activeTab}`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: isPending ? 0.6 : 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className={isPending ? 'pointer-events-none' : ''}
      >
        {activeTab === 'extensions' && (
          <TabAccessGate module="extensions">
            <ExtensionsTab customerId={selectedCustomerId} />
          </TabAccessGate>
        )}
        {activeTab === 'trunks' && (
          <TabAccessGate module="trunks">
            <TrunksTab customerId={selectedCustomerId} />
          </TabAccessGate>
        )}
        {activeTab === 'ivrs' && (
          <TabAccessGate module="ivrs">
            <IvrsTab customerId={selectedCustomerId} />
          </TabAccessGate>
        )}
        {activeTab === 'routes' && (
          <TabAccessGate module="routes">
            <RoutesTab customerId={selectedCustomerId} />
          </TabAccessGate>
        )}
        {activeTab === 'phones' && (
          <TabAccessGate module="phones">
            <PhonesTab customerId={selectedCustomerId} />
          </TabAccessGate>
        )}
        {activeTab === 'audit' && (
          <TabAccessGate module="auditLog">
            <AuditTab customerId={selectedCustomerId} />
          </TabAccessGate>
        )}
      </motion.div>
    </div>
  );
}